import React, { useMemo, useState } from 'react';

import { Button } from '@flywire/headlessui-tailwindcss';
import { useFormState, useValidate } from '@flywire/react-hooks';

import { PhoneInputField } from 'components/FieldList/Field/PhoneInputField/PhoneInputField';
import { TextInputField } from 'components/FieldList/Field/TextInputField/TextInputField';
import { Heading } from 'components/Heading/Heading';
import { Modal } from 'components/ui/Modal/Modal';
import { NOOP } from 'constants/index';
import { InfoCircle } from 'icons';
import type { FieldValue, SanitizedField } from 'models';
import type { I18n } from 'utils';
import { constraints as constraintsCV } from 'utils/clientValidate/constraints';
import { createMarkup } from 'utils/createMarkup/createMarkup';

import './ContactInformationSection.scss';

type FormData = {
  [key: string]: string;
};

type Props = {
  fields: Record<string, SanitizedField>;
  i18n: I18n;
  onSubmit: (values: Record<string, FieldValue>) => void;
  trackEditDetails: () => void;
  trackSaveEditedDetails: () => void;
};

function ContactInformationSection({
  fields,
  i18n,
  onSubmit = NOOP,
  trackEditDetails,
  trackSaveEditedDetails,
}: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const initialValues = useMemo(
    () =>
      Object.keys(fields).reduce<{ [key: string]: string }>((acc, key) => {
        const field = fields[key];
        acc[field.id] = field.defaultValue as string;
        return acc;
      }, {}),
    [fields],
  );

  const constraints = useMemo(
    () =>
      constraintsCV.generate({
        fieldOrFields: Object.keys(fields).map(key => fields[key]),
      }),
    [fields],
  );

  const { values, update, dirtyFields } = useFormState(initialValues);
  // @ts-expect-error this is not properly typed
  const { isValid, errors } = useValidate(values, constraints);

  function handleOpenEditDetails() {
    trackEditDetails();
    setIsModalOpen(true);
  }

  function handleOnCancel() {
    setIsModalOpen(false);
  }

  function handleOnConfirm() {
    if (Object.keys(dirtyFields).length) {
      onSubmit(values as FormData);
    }
    trackSaveEditedDetails();
    setIsModalOpen(false);
  }

  return (
    <section className="Breakdown ContactInformationSection">
      <div className="Breakdown-header" />
      <div className="Breakdown-body">
        <div className="ContactInformationSection-Info">
          <InfoCircle />
        </div>
        <div className="ContactInformationSection-Content">
          <span
            dangerouslySetInnerHTML={createMarkup(
              i18n.t('paymentConfirmation.contactDetails', {
                email: fields.email.text as string,
                phone: fields.phone.text as string,
              }),
            )}
          />{' '}
          <button
            type="button"
            onClick={handleOpenEditDetails}
            data-testid="editContactDetails"
          >
            {i18n.t('paymentConfirmation.contactDetails.edit')}
          </button>
        </div>
      </div>

      <Modal
        allowClosing
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="medium"
      >
        <section>
          <header>
            <Heading as="h1" size="large" className="marginBottom-lg">
              {i18n.t('paymentConfirmation.contactDetails.modal.title')}
            </Heading>
          </header>
          <div className="flex flex-col gap-8">
            <TextInputField
              handleChange={update}
              field={{
                ...fields.email,
                error: errors.sender_email?.[0],
                value: fields.email.value
                  ? (fields.email.value as string)
                  : undefined,
                defaultValue: fields.email.defaultValue
                  ? (fields.email.defaultValue as string)
                  : undefined,
              }}
            />
            <PhoneInputField
              handleChange={update}
              field={{
                ...fields.phone,
                error: errors.sender_phone?.[0],
                value: fields.phone.value as string,
                defaultValue: fields.phone.defaultValue as string,
                format: fields.phone.format as string,
                label: fields.phone.label as string,
                name: fields.phone.name as string,
                section: fields.phone.section as string,
                type: fields.phone.type as string,
              }}
            />
            <div className="mt-2 flex flex-row justify-end gap-5">
              <Button
                tone="secondary"
                size="auto"
                data-testid="Modal-Button--cancel"
                onClick={handleOnCancel}
              >
                {i18n.t('modal.button.cancel')}
              </Button>
              <Button
                tone="primary"
                size="auto"
                data-testid="Modal-Button--confirm"
                onClick={handleOnConfirm}
                disabled={!isValid}
              >
                {i18n.t('modal.button.save')}
              </Button>
            </div>
          </div>
        </section>
      </Modal>
    </section>
  );
}

ContactInformationSection.displayName = 'ContactInformationSection';

export { ContactInformationSection };
